import {PlatformNavbarMenuItem} from "@flexinet/ui-components";
import {
    AiOutlineAppstore,
    AiOutlineBook,
    AiOutlineBug,
    AiOutlineBuild,
    AiOutlineCamera,
    AiOutlineCloudServer,
    AiOutlineCode,
    AiOutlineDashboard,
    AiOutlineDeploymentUnit,
    AiOutlineExperiment,
    AiOutlineEye,
    AiOutlineGroup,
    AiOutlineHistory,
    AiOutlineHome,
    AiOutlineLogin,
    AiOutlineOrderedList,
    AiOutlinePartition,
    AiOutlineRobot,
    AiOutlineSliders,
    AiOutlineTeam,
    AiOutlineThunderbolt,
    AiOutlineTool,
    AiOutlineUser,
    AiOutlineWallet
} from 'react-icons/ai'
import {rbacActions} from "../../../config/rbac/actions";
import {testingApiAvailable} from "../../../features/testing-api-integration/config";

export const getMenuItems = () => {

    // FNP-859: the synthetic-user catalog. The testing-api is not deployed to
    // every stage the console runs in (no prod endpoint by decision), so the
    // group is omitted entirely where the API is unreachable rather than
    // rendering links that cannot load.
    const autonomousTestingGroup = testingApiAvailable ? [{
        id: 'autonomous-testing-group',
        // FNP-904: the group now lands on Defects. An operator opening
        // "Autonomous Testing" wants to know what is broken — and a defect is
        // the answer at the right altitude, where an observation is raw evidence.
        link: '/defects',
        label: 'Autonomous Testing',
        icon: <AiOutlineExperiment/>,
        rbac: rbacActions.ListDefectSignatures,
        defaultOpened: false,
        links: [{
            // FNP-904: Defects leads the group. It is one row per DEFECT rather
            // than per observation — ~60 failing observations collapse to ~17
            // signatures — so it is the first screen that answers "what is
            // broken" without a human reading the raw table row by row.
            id: 'defects',
            link: '/defects',
            label: 'Defects',
            icon: <AiOutlineBug/>,
            rbac: rbacActions.ListDefectSignatures,
        }, {
            // The raw evidence beneath the defects. Kept directly below, because
            // triage regularly needs to drop from "this defect" to "the actual
            // wakes that produced it".
            id: 'test-observations',
            link: '/test-observations',
            label: 'Observations',
            icon: <AiOutlineEye/>,
            rbac: rbacActions.ListTestObservations,
        }, {
            // FNP-969: one row per RUN — one test definition executed by one
            // synthetic user during one wake. Sits in the OBSERVABILITY half,
            // directly below Observations: a run is the execution an observation
            // is evidence from, so triage drops defect -> observation -> run.
            // It is read-only (the executor writes runs over @aws_iam).
            id: 'test-runs',
            link: '/test-runs',
            label: 'Test Runs',
            icon: <AiOutlineHistory/>,
            rbac: rbacActions.ListTestRuns,
        }, {
            id: 'synthetic-users',
            link: '/synthetic-users',
            label: 'Synthetic Users',
            icon: <AiOutlineTeam/>,
            rbac: rbacActions.ListSyntheticUsers,
        }, {
            id: 'testing-actions',
            link: '/testing-actions',
            label: 'Actions',
            icon: <AiOutlineRobot/>,
            rbac: rbacActions.ListTestingActions,
        }, {
            // FNP-995: directly below Actions, because it answers the question
            // the Actions table raises — what does this actionCode actually DO,
            // and does a handler for it even exist in this deployment?
            id: 'testing-registry',
            link: '/testing-registry',
            label: 'Handler Registry',
            icon: <AiOutlineCode/>,
            rbac: rbacActions.ListRegisteredActions,
        }, {
            id: 'testing-personas',
            link: '/testing-personas',
            label: 'Personas',
            icon: <AiOutlineUser/>,
            rbac: rbacActions.ListTestingPersonas,
        }, {
            // FNP-967: authored test definitions. Sits with the other AUTHORING
            // surfaces, after the observability views, and directly below the
            // Actions it composes into an ordered sequence.
            id: 'test-definitions',
            link: '/test-definitions',
            label: 'Test Definitions',
            icon: <AiOutlineOrderedList/>,
            rbac: rbacActions.ListTestDefinitions,
        }]
    }] : [];

    return [
        {
            id: 'dashboard',
            link: '/dashboard',
            label: 'Dashboard',
            icon: <AiOutlineHome/>,
            rbac: rbacActions.ViewDashboard,
        }, {
            id: 'accounts',
            link: '/accounts',
            label: 'Accounts',
            icon: <AiOutlineWallet/>,
            rbac: rbacActions.ListAccounts,
        }, {
            id: 'users-group',
            link: '/users',
            label: 'Directory',
            icon: <AiOutlineGroup/>,
            rbac: 'ListUsers',
            defaultOpened: false,
            links: [{
                id: 'users',
                link: '/users',
                label: 'Users',
                icon: <AiOutlineLogin/>,
                rbac: rbacActions.ListUsers,
            }]
        }, {
            id: 'organizations',
            link: '/organizations',
            label: 'Organizations',
            icon: <AiOutlineCloudServer/>,
            rbac: rbacActions.ListOrganizations,
        }, {
            id: 'applications',
            link: '/applications',
            label: 'Applications',
            icon: <AiOutlineAppstore/>,
            rbac: rbacActions.ListApplications,
        }, {
            id: 'workflows-group',
            link: '/playbooks',
            label: 'Workflows',
            icon: <AiOutlinePartition/>,
            rbac: rbacActions.ListPlaybooks,
            defaultOpened: false,
            links: [{
                id: 'playbooks',
                link: '/playbooks',
                label: 'Playbooks',
                icon: <AiOutlineBook/>,
                rbac: rbacActions.ListPlaybooks,
            }, {
                id: 'executions',
                link: '/executions',
                label: 'Executions',
                icon: <AiOutlineThunderbolt/>,
                rbac: rbacActions.ListExecutions,
            }]
        }, {
            id: 'pipelines-group',
            link: '/pipelines',
            label: 'DevOps',
            icon: <AiOutlineBuild/>,
            rbac: rbacActions.ListPipelines,
            defaultOpened: false,
            links: [{
                id: 'pipelines',
                link: '/pipelines',
                label: 'Pipelines',
                icon: <AiOutlineBuild/>,
                rbac: rbacActions.ListPipelines,
            }, {
                id: 'code-pipelines',
                link: '/code-pipelines',
                label: 'Code Pipelines',
                icon: <AiOutlineDeploymentUnit/>,
                rbac: rbacActions.ListCodePipelines,
            }, {
                id: 'code-repositories',
                link: '/code-repositories',
                label: 'Repositories',
                icon: <AiOutlineCode/>,
                rbac: rbacActions.ListCodeRepositories,
            }, {
                id: 'deployment-account-groups',
                link: '/deployment-account-groups',
                label: 'Account Groups',
                icon: <AiOutlineGroup/>,
                rbac: rbacActions.ListDeploymentAccountGroups,
            }]
        }, {
            id: 'application-screenshots-group',
            link: '/application-screenshots',
            label: 'Tools',
            icon: <AiOutlineTool/>,
            rbac: rbacActions.ListApplicationScreenshots,
            defaultOpened: false,
            links: [{
                id: 'application-screenshots',
                link: '/application-screenshots',
                label: 'Screenshots',
                icon: <AiOutlineCamera/>,
                rbac: rbacActions.ListApplicationScreenshots,
            }]
        },
        ...autonomousTestingGroup,
        {
            // FNP-891 / FNP-893: platform-wide operational controls. Top-level and
            // always present — the whole point of this module is that the controls
            // (including the ones not wired up yet) are visible rather than living
            // only in a parameter store nobody remembers.
            id: 'platform-controls',
            link: '/platform-controls',
            label: 'Platform Controls',
            icon: <AiOutlineSliders/>,
            rbac: rbacActions.ListPlatformControls,
        }, {
            id: 'analytics',
            link: '/analytics',
            label: 'Analytics',
            icon: <AiOutlineDashboard/>,
            rbac: rbacActions.ListUsers,
        }] as PlatformNavbarMenuItem[];

};
